import { PropsWithChildren, Fragment, useLayoutEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { useAuth } from 'hooks/useAuth'
import { useStore } from 'hooks/useStore'
import { devMode } from 'utils/constants'
import { resolveAvatarFallbackSrc, resolveAvatarSrc } from 'utils/avatar'
import { IconSvg, AvatarImage } from 'components/common'
import { useModal } from 'contexts/modal-manager-context'

interface Props {
	position?: string
}

export function ProfileDropdown({ position }: PropsWithChildren<Props>) {
	const navigate = useNavigate()
	const { logout } = useAuth()
	const { user, clearStore, myEvents, myClubs, refetchMyEvents } = useStore()
	const resolvedAvatarSrc = resolveAvatarSrc(user)
	const [searchParams, setSearchParams] = useSearchParams()
	const { openModal } = useModal()
	const myClubSlug = useMemo(() => {
		return myClubs?.[0]?.slug
	}, [myClubs])

	useLayoutEffect(() => {
		refetchMyEvents()
	}, [])

	const removeQueryParams = () => {
		const param = searchParams.get('urlToken')
		if (param) {
			searchParams.delete('urlToken')
			setSearchParams(searchParams)
		}
	}

	return (
		<div>
			<Menu as="div" className="relative text-left">
				<div className="flex items-center">
					<MenuButton className="inline-flex items-center justify-between rounded-lg group">
						<div className="flex items-center min-w-0 flex-shrink-0">
							<AvatarImage
								className="size-8 rounded-full object-cover flex-shrink-0"
								src={resolvedAvatarSrc}
								fallbackSrc={resolveAvatarFallbackSrc(user)}
								alt={user?.userName}
							/>
						</div>
					</MenuButton>
				</div>
				<Transition
					as={Fragment}
					enter="transition ease-out duration-100"
					enterFrom="transform opacity-0 scale-95"
					enterTo="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leaveFrom="transform opacity-100 scale-100"
					leaveTo="transform opacity-0 scale-95"
				>
					<MenuItems
						className={clsx(
							'fixed inset-0 h-[100dvh] sm:absolute sm:inset-auto sm:h-auto sm:right-0 rounded-none sm:rounded-lg shadow-box bg-primary-surface/[0.4] backdrop-blur-lg focus:outline-none sm:mt-1 px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-4 sm:w-[300px] w-full z-50 sm:z-10 ring-1 ring-black ring-opacity-5 overflow-hidden flex flex-col border border-translucent-plus-border',
							{
								'origin-top-right': position !== 'top',
								'origin-bottom-right bottom-full': position === 'top',
							}
						)}
					>
						<div className="flex justify-between items-center flex-shrink-0 pb-2 border-b border-translucent-plus-border sm:border-none pl-3 pr-1 sm:pr-0">
							<div className="flex items-center gap-2 min-w-0 flex-1 text-left">
								<AvatarImage
									className="sm:hidden size-6 rounded-full object-cover flex-shrink-0"
									src={resolvedAvatarSrc}
									alt={user?.userName}
									/>
								<div className="min-w-0 flex-1 py-1">
									<p className="truncate font-medium text-left">{user?.name}</p>
								</div>
							</div>
							<MenuItem>
								{({ close }) => (
									<button
										type="button"
										onMouseDown={e => {
											e.preventDefault()
											e.stopPropagation()
										}}
										onClick={e => {
											e.preventDefault()
											e.stopPropagation()
											setTimeout(() => {
												close()
											}, 0)
										}}
										className="sm:hidden flex items-center justify-center size-8 rounded-full hover:bg-white/5 transition-colors"
										aria-label="Close profile menu"
									>
										<IconSvg icon="close" />
									</button>
								)}
							</MenuItem>
							<IconSvg icon="chevron-up-2" className="hidden sm:block" />
						</div>

						<div className="flex-1 overflow-auto space-y-1 mt-4">
							<MenuItem>
								{({ active }) => (
									<Link
										to="/profile/information"
										className={clsx(
											'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300',
											{
												'text-white': active,
												'text-primary-text': !active,
											}
										)}
									>
										<IconSvg icon="profile" aria-hidden="true" />
										<span>Profile information</span>
									</Link>
								)}
							</MenuItem>
							<hr className="border-white/10 my-2" />
							{Boolean(devMode('My Clubs (profile dropdown)')) && (
								<MenuItem>
									{({ active }) => (
										<Link
											to={
												myClubSlug
													? `/manage/rep-clubs/${myClubSlug}`
													: '/manage/rep-clubs/create'
											}
											className={clsx(
												'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300',
												{
													'text-white': active,
													'text-primary-text': !active,
												}
											)}
										>
											<IconSvg icon="repclub" aria-hidden="true" />
											<span>{myClubSlug ? 'My Clubs' : 'Create Clubs'}</span>
										</Link>
									)}
								</MenuItem>
							)}
							<MenuItem>
								{({ active }) => (
									<Link
										to={'/manage/rep-clubs/events'}
										className={clsx(
											'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300',
											{
												'text-white': active,
												'text-primary-text': !active,
												'pointer-events-none !text-white/10': myEvents.length === 0,
											}
										)}
									>
										<IconSvg
											icon="event"
											className={clsx({
												'pointer-events-none !text-white/10': myEvents.length === 0,
											})}
											aria-hidden="true"
										/>

										<span>Manage Events</span>
									</Link>
								)}
							</MenuItem>
							<MenuItem>
								{({ active }) => (
									<Link
										to="/profile/settings"
										className={clsx(
											'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300',
											{
												'text-white': active,
												'text-primary-text': !active,
											}
										)}
									>
										<IconSvg icon="settings" aria-hidden="true" />
										<span>Settings</span>
									</Link>
								)}
							</MenuItem>
							<MenuItem>
								{({ active }) => (
									<Link
										to="mailto:support@loyalink.com"
										className={clsx(
											'p-3 flex space-x-2 items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300',
											{
												'text-white': active,
												'text-primary-text': !active,
											}
										)}
									>
										<IconSvg icon="support" aria-hidden="true" />
										<span>Support</span>
									</Link>
								)}
							</MenuItem>
							{/* <MenuItem>
								{({ active }) => (
									<Link
										to="/profile/invites"
										className={clsx('py-3 flex space-x-2 text-sm items-center', {
											'text-white font-bold': active,
											'text-primary-text hover:text-white hover:font-bold': !active,
										})}
									>
										<IconSvg icon="profile-plus" aria-hidden="true" />
										<span>Invite Friends</span>
									</Link>
								)}
							</MenuItem> */}
						</div>
						<hr className="border-white/10 my-2" />
						<div className="flex-shrink-0">
							<MenuItem>
								<button
									type="button"
									className="p-3 flex space-x-2 w-full text-primary-text hover:text-white items-center hover:bg-secondary-surface/[0.4] rounded-lg transition-all duration-300"
									onClick={() => openModal('logout-confirmation', {
							onConfirm: () => logout(null, null, () => {
								removeQueryParams()
								clearStore()
								navigate('/auth/login', { replace: true })
							}),
						})}
								>
									<IconSvg icon="log-out" aria-hidden="true" />
									<span>Log Out</span>
								</button>
							</MenuItem>
						</div>
					</MenuItems>
				</Transition>
			</Menu>
		</div>
	)
}
